import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Box,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GavelIcon from '@mui/icons-material/Gavel';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 260;

const instances = [
  { name: 'LIVE', type: 'LIVE', strategy: 'BREAKOUT' }, // PRODUCTION - REAL MONEY!
  { name: 'TOPORB', type: 'TOPORB', strategy: 'BREAKOUT' }, // TIER2
  { name: 'TIER3', type: 'TIER3', strategy: 'BREAKOUT' }, // TIER3
  { name: 'FUTURES', type: 'FUTURES', strategy: 'FUTURES' }, // Futures trading
  { name: 'V1', type: 'V1', strategy: 'BREAKOUT' }, // OPTIONS
  { name: 'E_OPTIONS', type: 'E_OPTIONS', strategy: 'BREAKOUT' }, // E-OPTIONS
  { name: 'OPTIONS_PCS', type: 'OPTIONS_PCS', strategy: 'PCS' },
  { name: 'GROK', type: 'GROK', strategy: 'BREAKOUT' },
];

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Get authentication state
  const { user, logout, isAdmin, hasInstanceAccess } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Filter instances based on user permissions
  const visibleInstances = instances.filter(instance => 
    hasInstanceAccess(instance.name)
  );

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const getStrategyIcon = (strategy: string, instanceName?: string) => {
    if (strategy === 'LONG') {
      return <TrendingUpIcon sx={{ color: 'green', fontSize: 20 }} />;
    } else if (strategy === 'SHORT') {
      return <TrendingDownIcon sx={{ color: 'red', fontSize: 20 }} />;
    } else if (strategy === 'FUTURES') {
      // FUTURES: yellow
      return <AccountTreeIcon sx={{ color: '#f9a825', fontSize: 20 }} />;
    } else if (strategy === 'BREAKOUT' || strategy === 'HYBRID BREAKOUT' || strategy === 'PCS') {
      const upper = (instanceName || '').toUpperCase();
      // V1/E_OPTIONS/OPTIONS_PCS/GROK: purple (all options traders)
      if (upper === 'V1' || upper === 'E_OPTIONS' || upper === 'OPTIONS_PCS' || upper === 'GROK') {
        return <AccountTreeIcon sx={{ color: '#9c27b0', fontSize: 20 }} />;
      }
      // LIVE, TOPORB, TIER3: green (TIER2 style)
      if (upper === 'TOPORB' || upper === 'TIER3') {
        return <AccountTreeIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      }
      // Default: green
      return <AccountTreeIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    }
    return null;
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ 
          p: 2, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Trading Dashboard
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Welcome, {user?.username || 'User'}
            </Typography>
          </Box>
          {isMobile && (
            <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Divider />
        <List sx={{ px: 1, py: 2 }}>
          <ListItem 
            onClick={() => handleNavigation('/')}
            sx={location.pathname === '/' ? { 
              bgcolor: 'action.selected',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
              borderRadius: 2,
              mb: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
                transform: 'translateX(4px)',
                transition: 'all 0.2s'
              }
            } : {
              borderRadius: 2,
              mb: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
                transform: 'translateX(4px)',
                transition: 'all 0.2s'
              }
            }}
          >
            <ListItemIcon>
              <DashboardIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary={<Typography sx={{ fontWeight: location.pathname === '/' ? 'bold' : 500 }}>Dashboard</Typography>} 
            />
          </ListItem>
          
          {/* Monitoring - Admin Only */}
          {isAdmin && (
            <ListItem 
              onClick={() => handleNavigation('/monitoring')}
              sx={location.pathname === '/monitoring' ? { 
                bgcolor: 'action.selected',
                borderLeft: '4px solid',
                borderLeftColor: 'secondary.main',
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              } : {
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <ListItemIcon>
                <MonitorHeartIcon sx={{ color: '#f48fb1' }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography sx={{ fontWeight: location.pathname === '/monitoring' ? 'bold' : 500 }}>Monitoring</Typography>} 
              />
            </ListItem>
          )}

          {/* Reporting - Admin Only */}
          {isAdmin && (
            <ListItem 
              onClick={() => handleNavigation('/reporting')}
              sx={location.pathname === '/reporting' ? { 
                bgcolor: 'action.selected',
                borderLeft: '4px solid',
                borderLeftColor: 'info.main',
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              } : {
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <ListItemIcon>
                <AssessmentIcon sx={{ color: '#29b6f6' }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography sx={{ fontWeight: location.pathname === '/reporting' ? 'bold' : 500 }}>Reporting</Typography>} 
              />
            </ListItem>
          )}

          {/* VIX Tracker - Admin Only */}
          {isAdmin && (
            <ListItem 
              onClick={() => handleNavigation('/vix')}
              sx={location.pathname === '/vix' ? { 
                bgcolor: 'action.selected',
                borderLeft: '4px solid',
                borderLeftColor: 'warning.main',
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              } : {
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <ListItemIcon>
                <ShowChartIcon sx={{ color: '#ffa726' }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography sx={{ fontWeight: location.pathname === '/vix' ? 'bold' : 500 }}>VIX</Typography>} 
              />
            </ListItem>
          )}
          {/* FedTrade desk - Admin Only */}
          {isAdmin && (
            <ListItem 
              onClick={() => handleNavigation('/fedtrade')}
              sx={location.pathname === '/fedtrade' ? { 
                bgcolor: 'action.selected',
                borderLeft: '4px solid',
                borderLeftColor: 'secondary.main',
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              } : {
                borderRadius: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <ListItemIcon>
                <GavelIcon sx={{ color: '#ce93d8' }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography sx={{ fontWeight: location.pathname === '/fedtrade' ? 'bold' : 500 }}>FedTrade</Typography>} 
              />
            </ListItem>
          )}
        </List>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}>
            Trading Instances
          </Typography>
        </Box>
        <List sx={{ px: 1 }}>
          {visibleInstances.map((instance) => {
            // Map internal instance names to display names
            const displayNameMap: { [key: string]: string } = {
              'V1': 'OPTIONS',
              'E_OPTIONS': 'E-OPTIONS',
              'OPTIONS_PCS': 'OPTIONS PCS',
              'TOPORB': 'TIER2',
              'TIER3': 'TIER3',
              'LIVE': 'LIVE',
              'FUTURES': 'Futures'
            };
            const displayName = displayNameMap[instance.name] || instance.name;
            const isActive = location.pathname === `/instance/${instance.name}`;
            
            return (
              <ListItem
                key={instance.name}
                onClick={() => handleNavigation(`/instance/${instance.name}`)}
                sx={isActive ? {
                  bgcolor: 'action.selected',
                  borderLeft: '4px solid',
                  borderLeftColor: 'primary.main',
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s'
                  }
                } : {
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <ListItemIcon>
                  {getStrategyIcon(instance.strategy, instance.name)}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        component="span" 
                        sx={isActive ? { fontWeight: 'bold' } : { fontWeight: 500 }}
                      >
                        {displayName}
                      </Typography>
                    </Box>
                  } 
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold'
          }}
        >
          Logout
        </Button>
        
        {/* User Menu */}
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleUserMenuOpen}
            startIcon={
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem' }}>
                {user?.username.charAt(0).toUpperCase()}
              </Avatar>
            }
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              justifyContent: 'flex-start',
              px: 2,
            }}
          >
            <Box sx={{ textAlign: 'left', ml: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {user?.username}
              </Typography>
              <Chip 
                label={user?.role} 
                size="small" 
                color={user?.role === 'admin' ? 'primary' : 'default'}
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
            </Box>
          </Button>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.full_name}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={() => { 
                handleUserMenuClose(); 
                navigate('/profile'); 
              }}
            >
              <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
              Profile
            </MenuItem>
            {isAdmin && (
              <MenuItem 
                onClick={() => { 
                  handleUserMenuClose(); 
                  navigate('/users'); 
                }}
              >
                <ManageAccountsIcon sx={{ mr: 1, fontSize: 20 }} />
                User Management
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={logout}>
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar 
          position="fixed" 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: 3
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
              Trading Dashboard
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Navigation Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            boxShadow: isMobile ? 0 : 2,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Navigation;
